import { useState, useEffect, useCallback } from 'react';
import { ClientStats, dashboardService } from '../../../../api/cansense/dashboardApi';
import { extractApiError } from '../utils/errorUtils';

interface UseClientStatsReturn {
  clients: ClientStats[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch per-client statistics from the dashboard API.
 *
 * Returns an array of client stats including exporter counts,
 * website status, active alerts, and overall health status.
 *
 * @example
 * const { clients, loading, error, refetch } = useClientStats();
 */
export const useClientStats = (): UseClientStatsReturn => {
  const [clients, setClients] = useState<ClientStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getClients();
      setClients(data);
    } catch (err) {
      const errorMessage = extractApiError(err, 'Failed to fetch client stats');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return { clients, loading, error, refetch: fetchClients };
};
